import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Printer, ArrowLeft, ShieldCheck, Save, Download, Send } from "lucide-react";
import axios from "axios";
import { FIRM } from "@/lib/firm";
import { sha256 } from "@/lib/sha256";
import { dash, money, fmtDate, borrowerSigOf } from "@/pages/CollateralMotion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const useQuery = () => new URLSearchParams(useLocation().search);
const todayISO = () => new Date().toISOString().slice(0, 10);
const GSA_OBLIGEE = "United States of America, acting by and through the General Services Administration (GSA)";

const CursiveSig = ({ name }) => name
  ? <span className="lg-sig-cursive" style={{ fontSize: "22pt", lineHeight: 1 }}>{name.startsWith("/s/") ? name : `/s/ ${name}`}</span>
  : <span>______________________________</span>;

export const GSA_CSS = `
        .gb-sheet{background:#fff;color:#141410;max-width:8.5in;margin:0 auto 28px;padding:0.5in 0.55in;box-shadow:0 10px 40px rgba(0,0,0,.4);font-family:'Times New Roman',Georgia,serif;font-size:10pt;line-height:1.32;}
        .gb-fedhead{text-align:center;border-bottom:2px solid #14204a;padding-bottom:7px;margin-bottom:9px;}
        .gb-fedeyebrow{font-size:8pt;letter-spacing:.14em;color:#14204a;font-weight:700;text-transform:uppercase;}
        .gb-fedtitle{font-size:14pt;font-weight:700;margin:4px 0 2px;text-transform:uppercase;}
        .gb-fedsub{font-size:8pt;color:#444;font-style:italic;}
        .gb-formhdr{display:flex;justify-content:space-between;align-items:flex-end;border:2px solid #14204a;padding:5px 8px;margin:0 0 7px;background:#eef1f8;}
        .gb-sfno{font-size:8.5pt;font-weight:700;color:#14204a;letter-spacing:.05em;}
        .gb-sfname{font-size:12.5pt;font-weight:700;text-transform:uppercase;}
        .gb-sfrev{font-size:7pt;color:#666;text-align:right;max-width:46%;}
        .gb-sheet p{text-align:justify;margin:4px 0;}
        .gb-f{display:flex;gap:8px;padding:1.5px 6px;border:1px solid #d3d9e6;border-top:none;}
        .gb-f:first-of-type{border-top:1px solid #d3d9e6;}
        .gb-fl{flex:0 0 38%;font-weight:600;color:#222;font-size:9pt;text-transform:uppercase;letter-spacing:.02em;}
        .gb-fv{flex:1;font-size:9.5pt;}
        .gb-blank{display:inline-block;min-width:180px;border-bottom:1px solid #333;}
        .gb-grid2{display:flex;gap:0;} .gb-grid2>div{flex:1;}
        .gb-note{font-size:7.6pt;color:#5C5C5C;margin-top:3px;}
        .gb-h{font-size:9.5pt;font-weight:700;color:#14204a;margin:7px 0 3px;text-transform:uppercase;letter-spacing:.03em;}
        .gb-table{width:100%;border-collapse:collapse;margin:5px 0;font-size:8pt;}
        .gb-table th,.gb-table td{border:1px solid #b9c2d6;padding:2.5px 5px;text-align:left;vertical-align:top;}
        .gb-table th{background:#eef1f8;color:#14204a;font-weight:700;}
        .gb-sig{margin:7px 0;} .gb-sig p{margin:3px 0;font-size:9pt;}
        .gb-sigrow{display:flex;gap:20px;margin-top:6px;} .gb-sigrow>div{flex:1;}
        .gb-sigrow p{font-size:9pt;margin:2px 0;}
        .gb-form{page-break-before:always;page-break-inside:avoid;break-inside:avoid;}
        .gb-solo .gb-form,.gb-claimsolo .gb-form{page-break-before:auto!important;break-before:auto!important;}
        .gb-claimsolo{page-break-inside:avoid;}
        .gb-stamp{display:flex;gap:11px;align-items:center;margin:4px 0 2px;padding:4px 7px;border:1px solid #cfd8e6;border-radius:4px;background:#fff;page-break-inside:avoid;}
        .gb-medallion{flex:0 0 190px;position:relative;border:2px solid #1a7a3c;border-radius:6px;padding:3px 7px 4px;color:#0f6b34;background:repeating-linear-gradient(45deg,#f4fbf6,#f4fbf6 3px,#e6f5eb 3px,#e6f5eb 6px);transform:rotate(-2deg);box-shadow:inset 0 0 0 1.5px #1a7a3c,0 1px 2px rgba(0,0,0,.15);}
        .gb-med-prefix{position:absolute;top:3px;right:6px;font-size:12pt;font-weight:800;color:#1a7a3c;border:1.5px solid #1a7a3c;border-radius:50%;width:18px;height:18px;line-height:15px;text-align:center;}
        .gb-med-guar{font-size:8.5pt;font-weight:800;letter-spacing:.03em;color:#0f6b34;text-transform:uppercase;line-height:1;max-width:140px;}
        .gb-med-prog{font-size:5.4pt;font-weight:700;letter-spacing:.02em;color:#1a7a3c;text-transform:uppercase;margin-top:2px;line-height:1.05;}
        .gb-med-firm{font-size:5.8pt;font-weight:700;color:#0f6b34;margin-top:2px;border-top:1px solid #1a7a3c;padding-top:1px;}
        .gb-med-code{font-family:'Courier New',monospace;font-size:5.8pt;color:#0f6b34;margin-top:1px;}
        .gb-stamp-body{flex:1;font-size:7.4pt;color:#14204a;}
        .gb-stamp-body .gb-stamp-title{font-weight:700;letter-spacing:.04em;text-transform:uppercase;font-size:7.4pt;}
        .gb-stamp-body code{font-family:'Courier New',monospace;font-size:7.4pt;word-break:break-all;display:block;margin:2px 0;color:#14204a;}
        .gb-stamp-body .gb-stamp-meta{color:#5C5C5C;font-size:7pt;}
        .gb-reltable th{background:#eaf3ec;color:#0f6b34;}
        .gb-rel-status{color:#0a6b3f;font-weight:700;}
        .gb-mono-hash{font-family:'Courier New',monospace;font-size:8.5pt;word-break:break-all;color:#14204a;background:#eef1f8;padding:1px 4px;border-radius:3px;}
        .gb-claim{font-size:7.8pt;line-height:1.1;}
        .gb-claim p{margin:1.5px 0;text-align:justify;}
        .gb-claim .gb-h{margin:3px 0 1px;font-size:8.2pt;}
        .gb-cl-sub{font-size:7.4pt;color:#555;font-style:italic;text-align:center;margin:-2px 0 4px;}
        .gb-cl-line{border-bottom:1px solid #333;display:inline-block;min-width:120px;}
        .gb-cl-fill{border-bottom:1px solid #14204a;padding:0 4px;font-weight:600;}
        .gb-claim .gb-table{font-size:7.4pt;margin:2px 0;}
        .gb-claim .gb-table td,.gb-claim .gb-table th{padding:1.5px 4px;}
        .gb-cl-box{border:1px solid #b9c2d6;min-height:13px;padding:2px 5px;margin:0.5px 0;font-size:7.7pt;}
        .gb-cl-notice{font-size:7pt;color:#5C5C5C;border-top:1px solid #b9c2d6;margin-top:2px;padding-top:2px;}
        .gb-claim .gb-fedhead{padding-bottom:4px;margin-bottom:5px;}
        .gb-cl-chk{font-weight:700;}
        @media print{
          body{background:#fff!important;}
          .gb-workbench{background:#fff!important;min-height:0!important;}
          .no-print{display:none!important;}
          .cm-print-area{padding:0!important;max-width:none!important;}
          .gb-sheet{box-shadow:none!important;margin:0!important;max-width:none!important;padding:0.25in 0.35in!important;}
          .gb-h,.gb-sfname{break-after:avoid;}
          @page{size:letter;margin:0.3in;}
        }
`;

// Every form in the package, in binder order. Used for per-form isolated print/save.
export const GSA_FORMS = [
  { key: "cover", label: "Cover · Pledge & Assignment", short: "Cover" },
  { key: "sf28", label: "SF-28 · Affidavit of Individual Surety", short: "SF-28" },
  { key: "sf24", label: "SF-24 · Bid Bond", short: "SF-24" },
  { key: "sf25", label: "SF-25 · Performance Bond", short: "SF-25" },
  { key: "sf25a", label: "SF-25A · Payment Bond", short: "SF-25A" },
  { key: "of90", label: "OF-90 · Release of Lien on Real Property", short: "OF-90" },
  { key: "of91", label: "OF-91 · Release of Personal Property from Escrow", short: "OF-91" },
  { key: "claim", label: "Bond Claim & Collateral Submission Form", short: "Bond Claim" },
];

const B = () => <span className="gb-blank" />;
const F = ({ label, children }) => (
  <div className="gb-f"><span className="gb-fl">{label}</span><span className="gb-fv">{children || <B />}</span></div>
);
const FormHeader = ({ no, name, rev }) => (
  <div className="gb-formhdr">
    <div><div className="gb-sfno">STANDARD FORM {no}</div><div className="gb-sfname">{name}</div></div>
    <div className="gb-sfrev">{rev}</div>
  </div>
);
const val = (shared, key, auto) => (shared[key] !== undefined && shared[key] !== "" ? shared[key] : (auto || ""));

export const gsaDefaults = () => ({
  date: todayISO(), bondNo: "", txnNo: "", contractNo: "", obligeeMode: "gsa",
  principal: "", principalAddr: "", principalType: "Corporation",
  surety: FIRM.name, suretyAddr: FIRM.address,
  penalSum: "", project: "Bond collateral pledged in support of the referenced obligation and any award, contract, or financing arranged in connection therewith.",
  property: "", account: "", escrow: "", legalDesc: "", interestRate: "",
  pledgee: FIRM.tcmm.name, state: FIRM.notaryState,
});

export function GsaBondDoc({ app, shared, last, claimOnly, formKey }) {
  const binder = app.binder || {};
  const acctAuto = app.loan_account_no || app.account_no || app.loan_number || binder.accountNo || app.account_number || "";
  const propAuto = app.property_or_collateral || app.property || app.property_address || binder.collateral || "";
  const borrowerName = app.borrower_full_name || app.borrower_name || "";
  const lenderName = app.current_lender || "";
  const auto = {
    principal: [borrowerName, lenderName].filter(Boolean).join(" — "),
    principalAddr: app.current_lender_address || binder.lenderAddress || app.borrower_address || "",
    penalSum: app.payoff_amount || app.current_balance || binder.payoffAmount || "",
    property: propAuto,
    account: acctAuto,
    escrow: acctAuto,
    legalDesc: app.legal_description || binder.legalDescription || propAuto,
    interestRate: app.current_rate || binder.newRate || "",
  };
  const g = (k) => val(shared, k, auto[k]);
  const counterparty = app.current_lender || "";
  const obligee = shared.obligeeMode === "counterparty"
    ? (val(shared, "pledgee", counterparty) || counterparty || FIRM.tcmm.name)
    : GSA_OBLIGEE;
  const penal = g("penalSum");
  const bSig = borrowerSigOf(app);
  const firmSig = `/s/ ${FIRM.officer}`;
  const balance = app.current_balance || penal;
  const rate = g("interestRate");
  const countyState = [app.property_county || binder.propertyCounty, app.property_state || binder.propertyState].filter(Boolean).join(", ");
  const taxParcel = app.tax_parcel_id || binder.taxParcelId || "";

  // Auto reference numbers — deterministic per borrower file so cover + all forms match.
  const ref6 = String(parseInt(sha256(String(app.id || auto.principal || "seed")).slice(0, 8), 16)).slice(0, 6).padEnd(6, "0");
  const yr = (g("date") || todayISO()).slice(0, 4);
  const bondNo = g("bondNo") || `STF-GSA-${yr}-${ref6}`;
  const txnNo = g("txnNo") || `GSA-TXN-${yr}-${ref6}`;
  const account = g("account") || `STF-LN-${yr}-${ref6}`;
  const escrow = g("escrow") || account;
  const stampTime = `${(g("date") || todayISO())} · ${new Date().toISOString().slice(11, 19)} UTC`;
  const claimNo = `STF-CLM-${yr}-${ref6}`;

  // Per-form S.T.A.M.P. seal/hash — the formTag salts each hash so every form
  // carries its own distinct integrity digest.
  const baseTerms = [FIRM.name, FIRM.ein, bondNo, txnNo, auto.principal, obligee, penal, account, escrow, propAuto, g("date")]
    .map((x) => String(x == null ? "" : x)).join("|");
  const formHash = (tag) => sha256(`${tag}|${baseTerms}`);
  const medPrefix = String.fromCharCode(65 + (parseInt(ref6, 10) % 26));
  const StampSeal = ({ tag }) => (
    <div className="gb-stamp" data-testid={`gb-stamp-${tag}`}>
      <div className="gb-medallion">
        <div className="gb-med-prefix">{medPrefix}</div>
        <div className="gb-med-guar">Signature(s)<br />Guaranteed</div>
        <div className="gb-med-prog">Securities Transfer Medallion Program · S.T.A.M.P.</div>
        <div className="gb-med-firm">{FIRM.name} · Commercial Bancorp</div>
        <div className="gb-med-code">MEDALLION {medPrefix} · {formHash(tag).slice(0, 12).toUpperCase()}</div>
      </div>
      <div className="gb-stamp-body">
        <div className="gb-stamp-title">S.T.A.M.P. Medallion Seal — Securities Transfer Medallion Program · {tag}</div>
        <code>{formHash(tag)}</code>
        <div className="gb-stamp-meta">SHA-256 medallion authentication · {FIRM.name} (Commercial Bancorp) · EIN {FIRM.ein} · Executed {stampTime} · Bond {bondNo} · Txn {txnNo}. Tamper-evident: any alteration of the sealed terms yields a different hash.</div>
      </div>
    </div>
  );

  // Full release / satisfaction / closure of every obligation associated with the account.
  const releaseRows = [
    { item: "Loan / Account No.", ref: dash(account), status: "Released & Closed" },
    { item: "Escrow Account No.", ref: dash(escrow), status: "Released — all accounting closed" },
    { item: "Outstanding / Original Balance", ref: money(balance), status: "Satisfied in full" },
    { item: "Payoff Amount", ref: money(penal), status: "Paid & Satisfied" },
    { item: "Accrued Interest", ref: rate ? `${rate}% (accrued to date)` : "All accrued interest", status: "Settled to $0.00" },
    { item: "Mortgage / Lien(s)", ref: [propAuto, countyState].filter(Boolean).join(" · ") || "Recorded lien(s)", status: "Released of record" },
    { item: "Encumbrances", ref: "All encumbrances of record", status: "Released & Discharged" },
    { item: "Attachments / Levies / Garnishments", ref: "All attachments of record", status: "Discharged" },
    { item: "Security Interest (UCC-1)", ref: `Ref. ${bondNo}`, status: "Terminated (UCC-3)" },
  ];
  const ReleaseSchedule = () => (
    <>
      <div className="gb-h">Release, Satisfaction &amp; Closure of All Associated Obligations</div>
      <table className="gb-table gb-reltable"><thead><tr><th>Item</th><th>Reference / Amount</th><th>Disposition</th></tr></thead>
        <tbody>{releaseRows.map((r, i) => (
          <tr key={i}><td>{r.item}</td><td>{r.ref}</td><td className="gb-rel-status">{r.status}</td></tr>
        ))}</tbody></table>
      <p>All liens, loan amounts, principal, interest, encumbrances, attachments, levies, security interests, and every other obligation associated with the above account are hereby fully <strong>released, settled, satisfied, discharged and closed</strong>. Upon release of escrow, all related accounting, ledgers, sub-accounts and reserves are <strong>reconciled to a zero balance and permanently closed</strong>, and no further sums are owing by the Principal / Obligor.</p>
    </>
  );

  const SigBlock = ({ role, who, sig }) => (
    <div>
      <p style={{ fontWeight: 700 }}>{role}</p>
      <p>Signature: <CursiveSig name={sig} /></p>
      <p>Name: {who || <B />}</p>
      <p>Title: {sig === firmSig ? "Authorized Officer" : <B />}</p>
      <p>Date: {g("date") ? fmtDate(g("date")) : <B />}  ·  [Corporate Seal]</p>
    </div>
  );

  const claimBlock = (
      <div className="gb-form gb-claim" data-testid={`gb-claim-${app.id}`}>
        <div className="gb-fedhead">
          <div className="gb-fedeyebrow">Private Submission — Not a Government-Issued Form</div>
          <div className="gb-fedtitle" style={{ fontSize: "13pt" }}>Bond Claim &amp; Collateral Submission Form</div>
          <div className="gb-cl-sub">Related to submitted surety bonds and supporting collateral</div>
        </div>
        <F label="Date">{g("date") ? fmtDate(g("date")) : <B />}</F>
        <F label="Claim No.">{claimNo}</F>

        <div className="gb-h">I. Claimant / Principal</div>
        <F label="Name">{dash(borrowerName)}</F>
        <F label="Address">{dash(app.borrower_address)}</F>
        <F label="Telephone / Email">{dash([app.borrower_phone, app.borrower_email].filter(Boolean).join(" · "))}</F>

        <div className="gb-h">II. Bond Identification</div>
        <table className="gb-table"><thead><tr><th style={{ width: "10%" }}>Incl.</th><th style={{ width: "18%" }}>Form</th><th>Bond Type</th><th style={{ width: "26%" }}>Bond / Penal Sum</th></tr></thead>
          <tbody>
            <tr><td className="gb-cl-chk">[X]</td><td>SF 24</td><td>Bid Bond</td><td>{penal ? money(penal) : "—"}</td></tr>
            <tr><td className="gb-cl-chk">[X]</td><td>SF 25</td><td>Performance Bond</td><td>{penal ? money(penal) : "—"}</td></tr>
            <tr><td className="gb-cl-chk">[X]</td><td>SF 25A</td><td>Payment Bond</td><td>{penal ? money(penal) : "—"}</td></tr>
            <tr><td className="gb-cl-chk">[X]</td><td>SF 28</td><td>Individual Surety Affidavit</td><td>{penal ? money(penal) : "—"}</td></tr>
          </tbody></table>
        <F label="Solicitation / Contract No.">{g("contractNo") || dash(account)}</F>
        <F label="Principal">{dash(g("principal"))}</F>
        <F label="Obligee / Beneficiary">{dash(obligee)}</F>
        <F label="Surety / Individual Surety">{dash(g("surety"))}</F>
        <F label="Bond Date · Effective Date">{g("date") ? `${fmtDate(g("date"))} · ${fmtDate(g("date"))}` : <B />}</F>

        <div className="gb-h">III. Claim / Demand</div>
        <p style={{ margin: "2px 0" }}>Nature of Claim / Default / Loss / Performance Issue:</p>
        <div className="gb-cl-box">Demand for satisfaction and release of the referenced obligation and for a full accounting audit of the pledged bonds and surety. The Principal has tendered the surety bonds (SF-24/25/25A), the SF-28 Individual Surety Affidavit, and the pledged collateral above in full satisfaction (prepayment) of Loan / Account No. {dash(account)}, discharging the outstanding balance of {money(balance)}. The Creditor / Servicer's (including any assignee's) failure to process the accounting audit and to promptly refund the unearned portion of the finance charge upon this prepayment / acceleration constitutes <strong>unjust enrichment</strong> and a violation of <strong>15 U.S.C. § 1615</strong> (refund of unearned finance charge; payoff statement due within five days).</div>
        <F label="Amount Claimed">{penal ? money(penal) : <B />}</F>
        <p style={{ margin: "2px 0" }}>Requested Action / Remedy:</p>
        <div className="gb-cl-box">(1) Provide the payoff / prepayment statement and process a full accounting audit of the pledged bonds and surety; (2) refund the unearned finance charge under 15 U.S.C. § 1615 and disgorge all amounts unjustly retained; (3) release all liens, encumbrances, attachments and security interests (UCC-3); and (4) close Loan / Account No. {dash(account)} and Escrow No. {dash(escrow)} to a zero balance. This bond package (SF-24/25/25A/28, OF-90/91 and supporting collateral) is incorporated by reference and constitutes a legally binding attachment to the above loan account.</div>

        <div className="gb-h">IV. Collateral / Security Supporting Bond</div>
        <F label="Collateral / Pledged Asset">{dash([g("property"), "U.S. Treasury securities & Secured Negotiable Instrument"].filter(Boolean).join(" · "))}</F>
        <F label="Estimated / Stated Value">{penal ? money(penal) : <B />}</F>
        <F label="Supporting Document / Exhibit No.">{`Bond No. ${bondNo}`}</F>
        <F label="Custodian / Holder">{dash(g("pledgee"))}</F>

        <div className="gb-h">V. Supporting Documents Submitted</div>
        <p className="gb-cl-chk">[X] SF 24  [X] SF 25  [X] SF 25A  [X] SF 28  [X] Contract / Agreement  [X] Collateral Documentation  [X] Surety Documentation  [ ] Correspondence  [ ] Proof of Loss / Performance  [ ] Other</p>

        <div className="gb-h">VI. Certification and Request for Determination</div>
        <p>I certify that the foregoing information and attached documentation are submitted for review concerning the identified bond(s), and I request a written determination as to acceptance, additional documentation required, the claim procedure, and disposition.</p>
        <p>Claimant / Authorized Representative: <span className="gb-cl-fill">{dash(borrowerName)}</span></p>
        <p>Signature: <CursiveSig name={bSig} />  ·  Date: {g("date") ? fmtDate(g("date")) : <B />}</p>

        <div className="gb-h">VII. Recipient Acknowledgment — Receipt Only</div>
        <p>Received By: <span className="gb-cl-line" style={{ minWidth: 230 }}>&nbsp;</span>  ·  Signature: <span className="gb-cl-line" style={{ minWidth: 170 }}>&nbsp;</span></p>
        <p>Title / Department: <span className="gb-cl-line" style={{ minWidth: 180 }}>&nbsp;</span>  ·  Date Received: <span className="gb-cl-line" style={{ minWidth: 110 }}>&nbsp;</span>  ·  Reference No.: <span className="gb-cl-line" style={{ minWidth: 110 }}>&nbsp;</span></p>
        <p className="gb-cl-notice">ACKNOWLEDGMENT OF RECEIPT DOES NOT CONSTITUTE ACCEPTANCE, APPROVAL, ADMISSION OF LIABILITY, VALIDATION OF THE CLAIM, OR CREATION OF A LIEN OR SECURITY INTEREST. SF 24/25/25A/28 are federal acquisition forms prescribed for specified federal bonding situations; this submission form is privately prepared and does not itself create, modify, transfer, perfect, or enforce a bond, lien, security interest, or collateral arrangement.</p>
        <StampSeal tag="BOND CLAIM · COLLATERAL SUBMISSION" />
      </div>
  );

  const coverBlock = (
    <React.Fragment>
      {/* Cover / Pledge & Assignment */}
      <div className="gb-fedhead">
        <div className="gb-fedeyebrow">General Services Administration · Federal Acquisition Regulation 48 CFR 53.228</div>
        <div className="gb-fedtitle">GSA Bond Package &amp; Pledge of Bond Collateral</div>
        <div className="gb-fedsub">SF-28 · SF-24 · SF-25 · SF-25A · OF-90 · OF-91 — assembled for pledge to an authorized counterparty</div>
      </div>
      <F label="Package Reference / Bond No.">{bondNo}</F>
      <F label="Transaction No.">{txnNo}</F>
      <F label="Date">{g("date") ? fmtDate(g("date")) : ""}</F>
      <F label="Principal / Obligor">{g("principal")}</F>
      <F label="Principal Address">{g("principalAddr")}</F>
      <F label="Surety">{g("surety")}</F>
      <F label="Surety Address">{g("suretyAddr")}</F>
      <F label="Obligee">{obligee}</F>
      <F label="Penal Sum / Bond Amount">{penal ? money(penal) : ""}</F>
      <F label="Loan / Account No. (Escrow No.)">{dash(account)} · {dash(escrow)}</F>
      <F label="Property Address / Collateral">{dash(g("property"))}</F>
      <F label="Legal Description">{dash(g("legalDesc"))}</F>
      <F label="County / State · Tax Parcel ID">{[countyState, taxParcel].filter(Boolean).join(" · ") || dash("")}</F>

      <div className="gb-h">Pledge &amp; Assignment of Bond Collateral</div>
      <p>For value received, and to secure the referenced obligation, the Surety and Principal pledge, assign, and grant a security interest in the surety bonds assembled in this package (SF-24, SF-25, SF-25A, the SF-28 Affidavit of Individual Surety, and the OF-90 / OF-91 releases) in favor of <strong>{dash(g("pledgee"))}</strong> (the Secured Party / Pledgee), for credit to the account of {dash(g("principal"))}, Loan / Account No. {dash(account)} (Escrow No. {dash(escrow)}), secured by the real property at {dash(g("property"))}.</p>
      <div className="gb-h">Acceptance — Pledge, Transfer &amp; Assignment</div>
      <p>For value received, the foregoing bonds and collateral are hereby <strong>pledged, transferred and assigned</strong> to <strong>{dash(lenderName)}</strong> (the Lender / Servicer), for credit to and satisfaction of Loan / Account No. <strong>{dash(account)}</strong> (Escrow No. {dash(escrow)}), securing the obligation of {dash(borrowerName)}, and accepted by the Secured Party / Pledgee for the benefit of the Lender / Servicer. This package is incorporated by reference into, and constitutes a legally binding attachment to, the above Loan / Account.</p>
      <F label="Pledged, Transferred &amp; Assigned To (Lender / Servicer)">{dash(lenderName)}</F>
      <F label="Lender / Servicer Address">{dash(g("principalAddr"))}</F>
      <F label="Loan / Account No. (Escrow No.)">{dash(account)} · {dash(escrow)}</F>
      <F label="Secured Party / Pledgee">{dash(g("pledgee"))}</F>
      <div className="gb-sig">
        <p style={{ fontWeight: 700 }}>Accepted &amp; executed electronically by an authorized representative of the Secured Party</p>
        <p>Signature (electronic execution): <CursiveSig name={firmSig} /> — {FIRM.officer}, Authorized Officer, {g("pledgee")}</p>
        <p>Execution Date / Time: {stampTime}  ·  S.T.A.M.P. Medallion Execution Hash (SHA-256):</p>
        <p><span className="gb-mono-hash">{formHash("COVER · PLEDGE")}</span></p>
        <p>Pledgor signatures — Surety: <CursiveSig name={firmSig} /> ({g("surety")})  ·  Principal / Obligor: <CursiveSig name={bSig} /> ({dash(borrowerName)})</p>
      </div>
      <StampSeal tag="COVER · PLEDGE" />
    </React.Fragment>
  );

  const sf28Block = (
    <div className="gb-form">
      <FormHeader no="28" name="Affidavit of Individual Surety" rev="Rev. 10/2023 · Prescribed by GSA-FAR (48 CFR) 53.228(e)" />
      <p>In connection with the bonds executed as part of this package, the individual surety, being duly sworn, deposes and states the following:</p>
      <F label="Name of Individual Surety">{g("surety")}</F>
      <F label="Home Address">{g("suretyAddr")}</F>
      <F label="Legal Residence (State)">{g("state")}</F>
      <F label="Name &amp; Address of Principal">{[g("principal"), g("principalAddr")].filter(Boolean).join(" · ")}</F>
      <F label="Penal Sum of Bond">{penal ? money(penal) : ""}</F>
      <F label="Type of Organization (Principal)">{g("principalType")}</F>
      <F label="Bond No. / Transaction No.">{bondNo} · {txnNo}</F>
      <F label="Loan / Account No. (Escrow No.)">{dash(account)} · {dash(escrow)}</F>
      <F label="Property Secured">{dash(g("property"))}</F>
      <p className="gb-note">The individual surety must be a natural person and a U.S. citizen; reproduced signatures are not acceptable. Security interest in the pledged assets is granted to the United States (or the designated Secured Party) as required by FAR 28.203.</p>
      <div className="gb-sig">
        <p>Signature of Individual Surety: <CursiveSig name={firmSig} /></p>
        <p>Date: {g("date") ? fmtDate(g("date")) : <B />}</p>
        <p>Subscribed and sworn before me this ______ day of __________, 20____ — Notary Public, State of {dash(g("state"))}. [Notary Seal]</p>
      </div>
      <StampSeal tag="SF-28 · INDIVIDUAL SURETY" />
    </div>
  );

  const sf24Block = (
    <div className="gb-form">
      <FormHeader no="24" name="Bid Bond" rev="Rev. 10/2023 · Prescribed by GSA-FAR (48 CFR) 53.228(a)" />
      <div className="gb-grid2">
        <div><F label="Principal (Legal name & business address)">{[g("principal"), g("principalAddr")].filter(Boolean).join(" · ")}</F></div>
        <div><F label="Type of Organization">{g("principalType")}</F><F label="State of Incorporation">{g("state")}</F></div>
      </div>
      <F label="Surety(ies) (Name & business address)">{[g("surety"), g("suretyAddr")].filter(Boolean).join(" · ")}</F>
      <F label="Penal Sum of Bond">{penal ? money(penal) : ""}</F>
      <F label="Bid Date">{g("date") ? fmtDate(g("date")) : ""}</F>
      <F label="Bid Identification / Invitation No.">{g("contractNo") || txnNo}</F>
      <F label="Loan / Account No. (Escrow No.)">{dash(account)} · {dash(escrow)}</F>
      <F label="Property Secured / Collateral">{dash(g("property"))}</F>
      <p>KNOW ALL PERSONS BY THESE PRESENTS, that we, the Principal and Surety(ies), are firmly bound to the {obligee} (the Government) in the above penal sum. For payment of the penal sum, we bind ourselves, our heirs, executors, administrators, and successors, jointly and severally.</p>
      <p>THE CONDITION OF THIS OBLIGATION IS SUCH, that the Principal has submitted the bid identified above. The obligation is void if the Principal (a) upon acceptance by the Government of the bid within the period specified, executes the further contractual documents and gives the bond(s) required by the terms of the bid; or (b) in the event of failure to execute such documents and give such bonds, pays the Government for any cost of procuring the work which exceeds the amount of the bid.</p>
      <div className="gb-sigrow">
        <SigBlock role="Principal" who={g("principal")} sig={bSig} />
        <SigBlock role="Corporate Surety" who={g("surety")} sig={firmSig} />
      </div>
      <StampSeal tag="SF-24 · BID BOND" />
    </div>
  );

  const sf25Block = (
    <div className="gb-form">
      <FormHeader no="25" name="Performance Bond" rev="Rev. 10/2023 · Prescribed by GSA-FAR (48 CFR) 53.228(b)" />
      <F label="Principal (Legal name & business address)">{[g("principal"), g("principalAddr")].filter(Boolean).join(" · ")}</F>
      <F label="Surety(ies) (Name & business address)">{[g("surety"), g("suretyAddr")].filter(Boolean).join(" · ")}</F>
      <F label="Penal Sum of Bond">{penal ? money(penal) : ""}</F>
      <F label="Contract No. / Date">{[g("contractNo") || txnNo, g("date") ? fmtDate(g("date")) : ""].filter(Boolean).join(" · ")}</F>
      <F label="Loan / Account No. (Escrow No.)">{dash(account)} · {dash(escrow)}</F>
      <F label="Property Secured / Collateral">{dash(g("property"))}</F>
      <p>KNOW ALL PERSONS BY THESE PRESENTS, that we, the Principal and Surety(ies), are firmly bound to the {obligee} in the above penal sum. For payment of the penal sum, we bind ourselves, our heirs, executors, administrators, and successors, jointly and severally.</p>
      <p>THE CONDITION OF THIS OBLIGATION IS SUCH, that the Principal entered into the contract identified above; therefore, the obligation is void if the Principal (a) performs and fulfills all the undertakings, covenants, terms, conditions, and agreements of the contract during the original term and any extensions thereof that are granted by the Government, with or without notice to the Surety(ies), and during the life of any guaranty required under the contract; and (b) performs and fulfills all the undertakings, covenants, terms, conditions, and agreements of any and all duly authorized modifications of the contract.</p>
      <div className="gb-sigrow">
        <SigBlock role="Principal" who={g("principal")} sig={bSig} />
        <SigBlock role="Corporate Surety" who={g("surety")} sig={firmSig} />
      </div>
      <StampSeal tag="SF-25 · PERFORMANCE BOND" />
    </div>
  );

  const sf25aBlock = (
    <div className="gb-form">
      <FormHeader no="25A" name="Payment Bond" rev="Rev. 10/2023 · Prescribed by GSA-FAR (48 CFR) 53.228(c)" />
      <div className="gb-fedsub" style={{ marginBottom: 6 }}>(For the protection of persons supplying labor and material)</div>
      <F label="Principal (Legal name & business address)">{[g("principal"), g("principalAddr")].filter(Boolean).join(" · ")}</F>
      <F label="Surety(ies) (Name & business address)">{[g("surety"), g("suretyAddr")].filter(Boolean).join(" · ")}</F>
      <F label="Penal Sum of Bond">{penal ? money(penal) : ""}</F>
      <F label="Contract No. / Date">{[g("contractNo") || txnNo, g("date") ? fmtDate(g("date")) : ""].filter(Boolean).join(" · ")}</F>
      <F label="Loan / Account No. (Escrow No.)">{dash(account)} · {dash(escrow)}</F>
      <F label="Property Secured / Collateral">{dash(g("property"))}</F>
      <p>KNOW ALL PERSONS BY THESE PRESENTS, that we, the Principal and Surety(ies), are firmly bound to the {obligee} in the above penal sum. For payment of the penal sum, we bind ourselves, our heirs, executors, administrators, and successors, jointly and severally.</p>
      <p>THE CONDITION OF THIS OBLIGATION IS SUCH, that the Principal entered into the contract identified above; therefore, the obligation is void if the Principal promptly makes payment to all persons having a direct relationship with the Principal or a subcontractor of the Principal for furnishing labor, material, or both in the prosecution of the work provided for in the contract, and any authorized modifications of the contract that subsequently are made.</p>
      <div className="gb-sigrow">
        <SigBlock role="Principal" who={g("principal")} sig={bSig} />
        <SigBlock role="Corporate Surety" who={g("surety")} sig={firmSig} />
      </div>
      <StampSeal tag="SF-25A · PAYMENT BOND" />
    </div>
  );

  const of90Block = (
    <div className="gb-form">
      <FormHeader no="OF-90 (Legacy)" name="Release of Lien on Real Property" rev="Optional Form 90 — cancelled/obsolete by GSA; recreated for release purposes" />
      <F label="Surety (Lienholder)">{g("surety")}</F>
      <F label="Principal / Owner">{g("principal")}</F>
      <F label="Real Property (Legal description / address)">{dash(g("property"))}</F>
      <F label="Legal Description">{dash(g("legalDesc"))}</F>
      <F label="County / State">{dash(countyState)}</F>
      <F label="Tax Parcel ID">{dash(taxParcel)}</F>
      <F label="Loan / Account No. (Escrow No.)">{dash(account)} · {dash(escrow)}</F>
      <F label="Recorded Lien Reference">{bondNo}</F>
      <p>FOR VALUE RECEIVED, the undersigned Surety hereby releases and discharges the lien held by it upon the real property described above, which lien was given as security in connection with the bonds identified in this package. The Surety warrants that it has full authority to execute this release and that upon recording this instrument the said lien — together with all mortgages, deeds of trust, assignments, encumbrances, attachments and security interests of record affecting the property — shall be fully released, satisfied and discharged of record.</p>
      <ReleaseSchedule />
      <div className="gb-sig">
        <p>Surety: <CursiveSig name={firmSig} /> — {FIRM.officer}, Authorized Officer, {g("surety")}</p>
        <p>Date: {g("date") ? fmtDate(g("date")) : <B />}  ·  [Corporate Seal]</p>
        <p>Acknowledged before me this ______ day of __________, 20____ — Notary Public, State of {dash(g("state"))}. [Notary Seal]</p>
      </div>
      <StampSeal tag="OF-90 · RELEASE OF LIEN" />
    </div>
  );

  const of91Block = (
    <div className="gb-form">
      <FormHeader no="OF-91" name="Release of Personal Property from Escrow" rev="Optional Form 91 (Rev. 01/1990) · Active" />
      <F label="Depositor / Surety">{g("surety")}</F>
      <F label="Principal / Owner">{g("principal")}</F>
      <F label="Escrow Agent / Custodian">{dash(g("pledgee"))}</F>
      <F label="Escrow / Account Reference (= Loan / Account No.)">{dash(escrow)}</F>
      <F label="Bond No. / Transaction No.">{bondNo} · {txnNo}</F>
      <p>The personal property and instruments heretofore deposited and held in escrow as security in connection with the bonds identified in this package are hereby released from escrow and returned/redirected to the Principal. The Surety and Principal authorize the Escrow Agent / Custodian to effect this release.</p>
      <ReleaseSchedule />
      <p><strong>Closure of Escrow &amp; Accounting.</strong> Upon this release of escrow, the escrow account (No. {dash(escrow)}) and all related sub-accounts, ledgers, reserves and accounting entries are hereby <strong>reconciled to a zero balance, fully settled and permanently closed</strong>. No further deposits, holdbacks, interest or sums remain owing, and the associated loan / account No. {dash(account)} is closed in full.</p>
      <div className="gb-sigrow">
        <SigBlock role="Surety" who={g("surety")} sig={firmSig} />
        <SigBlock role="Principal / Owner" who={g("principal")} sig={bSig} />
      </div>
      <F label="Escrow Agent / Custodian — Authorized Signature">{""}</F>
      <StampSeal tag="OF-91 · RELEASE OF ESCROW" />
    </div>
  );

  const blocks = {
    cover: coverBlock, sf28: sf28Block, sf24: sf24Block, sf25: sf25Block,
    sf25a: sf25aBlock, of90: of90Block, of91: of91Block, claim: claimBlock,
  };

  const fk = formKey || (claimOnly ? "claim" : null);

  // Isolated single-form print/save (page-break neutralized so no leading blank page).
  if (fk && blocks[fk]) {
    return (
      <div className={`gb-sheet gb-solo${fk === "claim" ? " gb-claimsolo" : ""}`} data-testid={`gb-solo-${fk}-${app.id}`} style={{ pageBreakAfter: last ? "auto" : "always" }}>
        {blocks[fk]}
      </div>
    );
  }

  return (
    <div className="gb-sheet" data-testid={`gb-doc-${app.id}`} style={{ pageBreakAfter: last ? "auto" : "always" }}>
      {coverBlock}
      {sf28Block}
      {sf24Block}
      {sf25Block}
      {sf25aBlock}
      {of90Block}
      {of91Block}
      {claimBlock}
    </div>
  );
}

const Ctl = ({ label, value, onChange, type = "text", options }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1">{label}</label>
    {options
      ? <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#14204a]">
          {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      : <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#14204a]" />}
  </div>
);

export default function GsaBondPackage() {
  const { appId } = useParams();
  const q = useQuery();
  const idsParam = q.get("ids");
  const claimOnly = q.get("claim") === "1";
  const [formSel, setFormSel] = useState(() => q.get("form") || (claimOnly ? "claim" : ""));
  const activeForm = formSel || null;
  const activeFormLabel = (GSA_FORMS.find((f) => f.key === formSel) || {}).label || "";
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [shared, setShared] = useState(gsaDefaults);
  const set = (k) => (v) => setShared((p) => ({ ...p, [k]: v }));

  const token = localStorage.getItem("th_admin_token");
  const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

  useEffect(() => {
    const ids = idsParam ? idsParam.split(",").map((x) => x.trim()).filter(Boolean) : (appId ? [appId] : []);
    (async () => {
      const results = [];
      for (const id of ids) {
        try { const r = await axios.get(`${API}/borrower/applications/${id}`, cfg); if (r.data.application) results.push(r.data.application); } catch { /* skip */ }
      }
      if (results[0]) {
        const savedSettings = (results.find((a) => a.gsa_bond_settings) || {}).gsa_bond_settings || null;
        if (savedSettings) setShared((p) => ({ ...p, ...savedSettings }));
      }
      setApps(results);
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, [appId, idsParam]);

  const saveDocData = async () => {
    if (!apps.length) return;
    setSaving(true); setSavedMsg("");
    try {
      await Promise.all(apps.map((a) => axios.post(`${API}/admin/borrower-applications/${a.id}/motion-data`,
        { gsa_bond_settings: shared }, cfg)));
      setSavedMsg("Saved to file ✓"); setTimeout(() => setSavedMsg(""), 4000);
    } catch { setSavedMsg("Save failed"); setTimeout(() => setSavedMsg(""), 4000); }
    finally { setSaving(false); }
  };

  const [dlBusy, setDlBusy] = useState(false);
  const downloadPdf = async () => {
    if (!apps.length) return;
    setDlBusy(true);
    try {
      const r = await axios.post(`${API}/admin/gsa-bond/bulk-pdf`, { app_ids: apps.map((a) => a.id), claim_only: claimOnly, form: activeForm }, { ...cfg, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      const base = activeForm ? `gsa_${activeForm}` : "gsa_bond_package";
      a.href = url; a.download = apps.length > 1 ? `${base}_${apps.length}.pdf` : `${base}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || "Could not build the merged PDF."); }
    finally { setDlBusy(false); }
  };

  const [faxOpen, setFaxOpen] = useState(false);
  const [faxTo, setFaxTo] = useState(FIRM.tcmm.fax);
  const [faxBusy, setFaxBusy] = useState(false);
  const [faxMsg, setFaxMsg] = useState(null);
  const sendFax = async () => {
    if (!apps.length || !faxTo.trim()) return;
    setFaxBusy(true); setFaxMsg(null);
    try {
      const r = await axios.post(`${API}/admin/gsa-bond/efax`, { app_ids: apps.map((a) => a.id), to: faxTo.trim(), claim_only: claimOnly, form: activeForm, label: `${activeForm ? activeFormLabel : "GSA Bond Package"} · ${apps.length} borrower(s)` }, cfg);
      const fid = r.data?.fax?.fax_id || r.data?.fax?.id || "";
      const st = r.data?.fax?.status || "queued";
      setFaxMsg({ ok: true, text: `✓ Fax submitted to ${faxTo.trim()} — ${apps.length} package(s). Telnyx ID: ${fid || "n/a"} · Status: ${st}` });
    } catch (e) { setFaxMsg({ ok: false, text: e.response?.data?.detail || "Fax failed to send." }); }
    finally { setFaxBusy(false); }
  };

  if (loading) return <div style={{ padding: 40, color: "#ECECE4", fontFamily: "serif" }}>Loading…</div>;

  return (
    <div className="gb-workbench" style={{ background: "#0B0B07", minHeight: "100vh" }}>
      <div className="no-print" style={{ background: "#0E1020", borderBottom: "1px solid #14204a", padding: "12px 18px" }}>
        <div style={{ maxWidth: "8.5in", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <a href="/admin" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#9DB4E8", fontSize: 12 }}><ArrowLeft size={14} /> Back</a>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#9DB4E8", fontWeight: 700, letterSpacing: ".06em", fontSize: 12 }}>
            <ShieldCheck size={16} /> {activeForm ? (GSA_FORMS.find((f) => f.key === formSel) || {}).short?.toUpperCase() : "GSA BOND PACKAGE"}{apps.length > 1 ? ` · ${apps.length} BORROWERS` : ""}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <select value={formSel} onChange={(e) => setFormSel(e.target.value)} data-testid="gb-form-select"
              style={{ background: "#0F0F0A", color: "#ECECE4", border: "1px solid #3a4a86", borderRadius: 6, padding: "9px 10px", fontSize: 11, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", cursor: "pointer", maxWidth: 260 }}>
              <option value="">Full Package — all 8 forms</option>
              {GSA_FORMS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
            <button onClick={saveDocData} disabled={saving || !apps.length} data-testid="gb-save" style={btn("#9DB4E8", "#3a4a86")}><Save size={13} /> {saving ? "Saving…" : "Save to File"}</button>
            <button onClick={downloadPdf} disabled={dlBusy || !apps.length} data-testid="gb-download" style={btn("#9DB4E8", "#3a4a86")}><Download size={13} /> {dlBusy ? "Building…" : "Download PDF"}</button>
            <button onClick={() => setFaxOpen((x) => !x)} disabled={!apps.length} data-testid="gb-efax-toggle" style={btn("#5FBFA0", "#2E6F5E")}><Send size={13} /> eFax</button>
            <button onClick={() => window.print()} data-testid="gb-print" style={{ ...btn("#0B0B07", "#B8860B"), background: "#E4C97A", color: "#0B0B07" }}><Printer size={13} /> Print / Save as PDF</button>
          </div>
        </div>
        {savedMsg && <div className="text-[12px] text-[#5FE0B0] mt-2" data-testid="gb-saved-msg" style={{ maxWidth: "8.5in", margin: "6px auto 0" }}>{savedMsg}</div>}

        {faxOpen && (
          <div style={{ maxWidth: "8.5in", margin: "10px auto 0" }} className="rounded-md border border-[#2E6F5E]/40 bg-[#0F1410] p-3" data-testid="gb-efax-panel">
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#5FBFA0] mb-2 flex items-center gap-2"><Send size={12} /> eFax {activeForm ? activeFormLabel : "GSA Bond Package"}{apps.length > 1 ? ` (all ${apps.length} borrowers merged)` : ""}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ minWidth: 220 }}>
                <label className="block text-[10px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">Saved destination</label>
                <select onChange={(e) => e.target.value && setFaxTo(e.target.value)} data-testid="gb-efax-dir" className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4]">
                  <option value="">Choose…</option>
                  <option value={FIRM.tcmm.fax}>★ TCMM — {FIRM.tcmm.agent} · {FIRM.tcmm.fax}</option>
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 220 }}>
                <label className="block text-[10px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">Destination fax number</label>
                <input value={faxTo} onChange={(e) => setFaxTo(e.target.value)} data-testid="gb-efax-to" placeholder="+1 866 707 6574"
                  className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#2E6F5E]" />
              </div>
              <button onClick={sendFax} disabled={faxBusy || !faxTo.trim()} data-testid="gb-efax-send" style={{ ...btn("#0B0B07", "#2E6F5E"), background: "#5FBFA0", color: "#0B0B07" }}><Send size={13} /> {faxBusy ? "Sending…" : "Send eFax"}</button>
            </div>
            {faxMsg && <div data-testid="gb-efax-msg" className={`text-[12px] font-bold mt-2 ${faxMsg.ok ? "text-[#3DBB8E]" : "text-[#FF8585]"}`}>{faxMsg.text}</div>}
          </div>
        )}

        <div style={{ maxWidth: "8.5in", margin: "12px auto 0" }} className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Ctl label="Package Ref / Bond No." value={shared.bondNo} onChange={set("bondNo")} />
          <Ctl label="Transaction No." value={shared.txnNo} onChange={set("txnNo")} />
          <Ctl label="Date" value={shared.date} onChange={set("date")} type="date" />
          <Ctl label="Contract / Award No." value={shared.contractNo} onChange={set("contractNo")} />
          <Ctl label="Obligee" value={shared.obligeeMode} onChange={set("obligeeMode")} options={[{ v: "gsa", l: "United States / GSA" }, { v: "counterparty", l: "Counterparty / TCMM" }]} />
          <Ctl label="Principal / Obligor (override)" value={shared.principal} onChange={set("principal")} />
          <Ctl label="Principal Address (override)" value={shared.principalAddr} onChange={set("principalAddr")} />
          <Ctl label="Principal Type" value={shared.principalType} onChange={set("principalType")} options={[{ v: "Corporation", l: "Corporation" }, { v: "Partnership", l: "Partnership" }, { v: "Individual", l: "Individual" }, { v: "Joint Venture", l: "Joint Venture" }]} />
          <Ctl label="Surety" value={shared.surety} onChange={set("surety")} />
          <Ctl label="Surety Address" value={shared.suretyAddr} onChange={set("suretyAddr")} />
          <Ctl label="Penal Sum / Bond Amount ($)" value={shared.penalSum} onChange={set("penalSum")} type="number" />
          <Ctl label="Secured Party / Pledgee" value={shared.pledgee} onChange={set("pledgee")} />
          <Ctl label="Collateral / Property (override)" value={shared.property} onChange={set("property")} />
          <Ctl label="Legal Description (override)" value={shared.legalDesc} onChange={set("legalDesc")} />
          <Ctl label="Acknowledgment State" value={shared.state} onChange={set("state")} />
        </div>
        <div className="text-[10px] text-[#8A8A8A] mt-2" style={{ maxWidth: "8.5in", margin: "8px auto 0" }}>Principal, address, penal sum and collateral auto-populate from each borrower's file; anything blank prints as a fillable line. All bonds are jointly executed by the Principal (borrower e-sign) and Surety ({FIRM.name}). This package is pledgeable to {shared.pledgee || FIRM.tcmm.name}.</div>
      </div>

      <div className="cm-print-area" style={{ padding: "24px 0" }}>
        {!apps.length
          ? <div style={{ color: "#9A9A90", textAlign: "center", padding: 40 }}>No application loaded. Open this from a borrower file, or pass ?ids=… for bulk.</div>
          : apps.map((a, i) => <GsaBondDoc key={a.id} app={a} shared={shared} last={i === apps.length - 1} formKey={activeForm} />)}
      </div>
      <style>{GSA_CSS}</style>
    </div>
  );
}

const btn = (color, border) => ({ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", color, border: `1px solid ${border}`, padding: "9px 14px", borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", cursor: "pointer" });
